/**
 * Resume Comparison Utilities
 *
 * Functions to compare two resume versions and identify differences
 */

import { Resume } from '../services/resume';
import { ResumeDifference } from '../types/resumeTuning';

/**
 * Deep equality check for objects or arrays
 */
function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => deepEqual(item, b[index]));
  }
  
  if (typeof a === 'object' && a !== null && b !== null) {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every(key => deepEqual(a[key], b[key]));
  }
  
  return false;
}

/**
 * Compare two resume versions and return structured differences
 */
export function compareResumes(baseResume: Resume, comparedResume: Resume): ResumeDifference[] {
  const differences: ResumeDifference[] = [];
  
  // Compare summary
  const summarySame = deepEqual(baseResume.data.summary, comparedResume.data.summary);
  if (!summarySame) {
    differences.push({
      section: 'summary',
      original: baseResume.data.summary || '',
      customized: comparedResume.data.summary || '',
      changed: true,
      relevanceImpact: 10,
    });
  }
  
  // Compare experience
  const experienceSame = deepEqual(baseResume.data.experience, comparedResume.data.experience);
  if (!experienceSame) {
    differences.push({
      section: 'experience',
      original: baseResume.data.experience.map((e) => `${e.position} @ ${e.company}`),
      customized: comparedResume.data.experience.map((e) => `${e.position} @ ${e.company}`),
      changed: true,
      relevanceImpact: 25,
    });
  }
  
  // Compare skills
  const skillsSame = deepEqual(baseResume.data.skills, comparedResume.data.skills);
  if (!skillsSame) {
    const baseSkills = [
      ...(baseResume.data.skills.technical || []),
      ...(baseResume.data.skills.soft || []),
    ];
    const comparedSkills = [
      ...(comparedResume.data.skills.technical || []),
      ...(comparedResume.data.skills.soft || []),
    ];
    
    differences.push({
      section: 'skills',
      original: baseSkills,
      customized: comparedSkills,
      changed: true,
      relevanceImpact: 15,
    });
  }
  
  // Compare achievements (custom section)
  const baseAchievements = baseResume.data.projects?.map((p) => p.name) || [];
  const comparedAchievements = comparedResume.data.projects?.map((p) => p.name) || [];
  if (!deepEqual(baseAchievements, comparedAchievements)) {
    differences.push({
      section: 'achievements',
      original: baseAchievements,
      customized: comparedAchievements,
      changed: true,
      relevanceImpact: 20,
    });
  }
  
  return differences;
}

/**
 * Highlight differences in text (simple approach: show original → customized)
 */
export function formatDifference(diff: ResumeDifference): {
  label: string;
  before: string;
  after: string;
} {
  const label = diff.section.charAt(0).toUpperCase() + diff.section.slice(1);
  
  const before = Array.isArray(diff.original)
    ? diff.original.join(', ')
    : (diff.original as string);
  
  const after = Array.isArray(diff.customized)
    ? diff.customized.join(', ')
    : (diff.customized as string);
  
  return { label, before, after };
}
